import { useState, useEffect, useMemo } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { GraduationCap, ArrowRight, Loader2, Users, Eye, Bus } from "lucide-react";
import { initials } from "@/lib/helpers";

function ClassSectionPicker({ classes, name, section, onName, onSection, testid }) {
  const names = useMemo(() => Array.from(new Set(classes.map((c) => c.name).filter(Boolean))), [classes]);
  const sections = useMemo(
    () => Array.from(new Set(classes.filter((c) => c.name === name && c.section).map((c) => c.section))),
    [classes, name]
  );
  return (
    <div className="grid grid-cols-2 gap-3 mt-3">
      <div>
        <Label className="text-xs text-slate-500">Class</Label>
        <Select value={name} onValueChange={onName}>
          <SelectTrigger className="mt-1 h-10" data-testid={`${testid}-class`}><SelectValue placeholder="Select class" /></SelectTrigger>
          <SelectContent>
            {names.length === 0 ? <div className="px-2 py-1.5 text-xs text-slate-400">No classes in this session</div> : names.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-slate-500">Section</Label>
        <Select value={section || "__none__"} onValueChange={(v) => onSection(v === "__none__" ? "" : v)} disabled={!name}>
          <SelectTrigger className="mt-1 h-10" data-testid={`${testid}-section`}><SelectValue placeholder={name ? "Select section" : "Pick class first"} /></SelectTrigger>
          <SelectContent>
            {sections.length === 0 ? <SelectItem value="__none__">— No section —</SelectItem> : sections.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function SessionSelect({ sessions, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">Session</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 h-10" data-testid={testid}><SelectValue placeholder="Select session" /></SelectTrigger>
        <SelectContent>{sessions.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}{s.status === "Active" ? " · Active" : ""}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function PromoteStudents() {
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [srcSession, setSrcSession] = useState("");
  const [dstSession, setDstSession] = useState("");
  const [srcName, setSrcName] = useState("");
  const [srcSection, setSrcSection] = useState("");
  const [dstName, setDstName] = useState("");
  const [dstSection, setDstSection] = useState("");
  const [applyTransport, setApplyTransport] = useState(true);
  const [oldFeesManage, setOldFeesManage] = useState("block_old");
  const [oldDuesLabel, setOldDuesLabel] = useState("Old Dues");
  const [students, setStudents] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/classes"), api.get("/sessions"), api.get("/settings")]).then(([cr, sr, setr]) => {
      const cls = cr.data || []; const ses = sr.data || []; const cur = setr.data?.academic_session || "";
      setClasses(cls); setSessions(ses);
      const names = ses.map((s) => s.name);
      const classSessions = new Set(cls.map((c) => c.academic_session).filter(Boolean));
      const active = ses.filter((s) => s.status === "Active");
      const pick = (cur && names.includes(cur)) ? cur
        : (active.find((s) => classSessions.has(s.name))?.name || active[0]?.name || names[0] || "");
      setSrcSession(pick); setDstSession(pick);
    }).catch(() => {});
  }, []);

  const srcClasses = useMemo(() => classes.filter((c) => (c.academic_session || "") === srcSession), [classes, srcSession]);
  const dstClasses = useMemo(() => classes.filter((c) => (c.academic_session || "") === dstSession), [classes, dstSession]);

  const resolveId = (list, name, section) => {
    const recs = list.filter((c) => c.name === name);
    const rec = recs.find((c) => (c.section || "") === (section || "")) || recs[0];
    return rec ? rec.id : "";
  };
  const srcId = resolveId(srcClasses, srcName, srcSection);
  const dstId = resolveId(dstClasses, dstName, dstSection);
  const srcSections = srcClasses.filter((c) => c.name === srcName && c.section).map((c) => c.section);
  const dstSections = dstClasses.filter((c) => c.name === dstName && c.section).map((c) => c.section);
  const srcNeedsSection = srcName && srcSections.length > 0 && !srcSection;
  const dstNeedsSection = dstName && dstSections.length > 0 && !dstSection;
  const srcLabel = `${srcName}${srcSection ? " - " + srcSection : ""}${srcSession ? " (" + srcSession + ")" : ""}`;
  const dstLabel = `${dstName}${dstSection ? " - " + dstSection : ""}${dstSession ? " (" + dstSession + ")" : ""}`;

  const loadPreview = async () => {
    if (!srcId) { toast.error("Select a source class"); return; }
    setLoading(true);
    try {
      const r = await api.get(`/students?class_id=${srcId}&limit=2000`);
      const list = r.data.students || [];
      setStudents(list);
      setSelected(new Set(list.map((s) => s.id)));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = students && students.length > 0 && selected.size === students.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(students.map((s) => s.id)));

  const sameTarget = srcId && dstId && srcId === dstId;
  const crossSession = srcSession && dstSession && srcSession !== dstSession;
  const canPromote = students && selected.size > 0 && dstId && !sameTarget && !dstNeedsSection;

  const doPromote = async () => {
    setPromoting(true);
    try {
      const r = await api.post("/students/promote", {
        student_ids: Array.from(selected), dest_class_id: dstId, dest_section: dstSection,
        dest_session: dstSession, src_session: srcSession, apply_transport: applyTransport,
        old_fees_manage: oldFeesManage, old_dues_label: oldDuesLabel.trim() || "Old Dues",
      });
      toast.success(`${r.data.promoted} student(s) promoted to ${dstLabel}`);
      setConfirmOpen(false);
      setStudents(null); setSelected(new Set());
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setPromoting(false); }
  };

  return (
    <div className="space-y-5" data-testid="promote-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2">
          <GraduationCap className="text-indigo-500" size={28} />Promote / Migrate Students
        </h1>
        <p className="text-slate-500">Move a class & section of students into their next class/session at year-end.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="grid md:grid-cols-[1fr_auto_1fr] gap-5 items-start">
          <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/60">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">From (Source)</div>
            <SessionSelect sessions={sessions} value={srcSession} onChange={(v) => { setSrcSession(v); setSrcName(""); setSrcSection(""); setStudents(null); }} testid="src-session" />
            <ClassSectionPicker classes={srcClasses} name={srcName} section={srcSection}
              onName={(v) => { setSrcName(v); setSrcSection(""); setStudents(null); }}
              onSection={(v) => { setSrcSection(v); setStudents(null); }} testid="src" />
          </div>
          <div className="hidden md:flex justify-center pt-20"><ArrowRight className="text-indigo-400" size={28} /></div>
          <div className="rounded-xl border border-indigo-200 p-4 bg-indigo-50/50">
            <div className="text-xs font-semibold uppercase tracking-wider text-indigo-600 mb-2">To (Destination)</div>
            <SessionSelect sessions={sessions} value={dstSession} onChange={(v) => { setDstSession(v); setDstName(""); setDstSection(""); }} testid="dst-session" />
            <ClassSectionPicker classes={dstClasses} name={dstName} section={dstSection}
              onName={(v) => { setDstName(v); setDstSection(""); }}
              onSection={(v) => setDstSection(v)} testid="dst" />
          </div>
        </div>

        <div className="border-t border-slate-100 mt-5 pt-4 grid md:grid-cols-2 gap-5">
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer" htmlFor="apply-transport">
              <Checkbox id="apply-transport" checked={applyTransport} onCheckedChange={(v) => setApplyTransport(!!v)} className="mt-0.5" data-testid="apply-transport" />
              <span>
                <span className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><Bus size={14} className="text-slate-400" />Apply transport to all existing transport students</span>
                <span className="text-xs text-slate-400">Keep each student's current transport in the new session. Uncheck to remove transport on migration.</span>
              </span>
            </label>
            <div>
              <Label className="text-sm font-medium text-slate-700">Old Dues label</Label>
              <p className="text-xs text-slate-400 mb-1.5">Name for the carried-over due line shown in the new session.</p>
              <Input value={oldDuesLabel} onChange={(e) => setOldDuesLabel(e.target.value)} placeholder="Old Dues" className="h-9 max-w-xs" data-testid="old-dues-label" />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700">Old Fees Manage</Label>
            <p className="text-xs text-slate-400 mb-1.5">How carried dues behave across sessions.</p>
            <Select value={oldFeesManage} onValueChange={setOldFeesManage}>
              <SelectTrigger className="h-auto min-h-10 py-2" data-testid="old-fees-manage"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="block_old">Keep Visible and block collect in old session (current session can collect)</SelectItem>
                <SelectItem value="keep_open">Keep open in old session (both sessions can collect)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <Button onClick={loadPreview} disabled={!srcId || srcNeedsSection || loading} className="gap-1.5" data-testid="preview-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}Preview Students
          </Button>
          {srcNeedsSection && <span className="text-sm text-amber-600 font-medium" data-testid="src-section-warn">Pick a source section.</span>}
          {sameTarget && <span className="text-sm text-rose-600 font-medium" data-testid="same-target-warn">Source and destination can't be the same class & section.</span>}
        </div>
      </div>

      {students !== null && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="preview-card">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 flex-wrap gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-600 flex-wrap">
              <Users size={16} className="text-slate-400" />
              <span className="font-semibold text-slate-800" data-testid="selected-count">{selected.size}</span> of {students.length} selected ·
              <span className="font-medium">{srcLabel || "—"}</span> <ArrowRight size={14} className="inline" /> <span className="font-medium text-indigo-600">{dstLabel || "—"}</span>
            </div>
            <Button onClick={() => setConfirmOpen(true)} disabled={!canPromote} className="gap-1.5" data-testid="promote-btn">
              <GraduationCap size={16} />Promote {selected.size} Student{selected.size !== 1 ? "s" : ""}
            </Button>
          </div>
          {dstNeedsSection && <div className="px-4 py-2 text-sm text-amber-600 font-medium border-b border-slate-100" data-testid="dst-section-warn">Pick a destination section to enable promotion.</div>}
          {students.length === 0 ? (
            <div className="py-16 text-center text-slate-400">No students in {srcLabel}.</div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-520px)]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2.5 w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all" /></th>
                    <th className="px-3 py-2.5 text-left">Student</th>
                    <th className="px-3 py-2.5 text-left">Roll No</th>
                    <th className="px-3 py-2.5 text-left">Current Class</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`promote-row-${s.admission_number}`}>
                      <td className="px-3 py-2.5"><Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`row-check-${s.admission_number}`} /></td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 overflow-hidden flex items-center justify-center text-xs font-semibold border border-slate-200 shrink-0">
                            {s.photo ? <img src={s.photo} alt={s.name} className="w-full h-full object-cover" /> : initials(s.name)}
                          </div>
                          <div><div className="font-medium text-slate-800">{s.name}</div><div className="text-xs text-slate-400">{s.admission_number}</div></div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">{s.roll_number || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-600">{`${s.class_name || ""} ${s.section || ""}`.trim() || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="promote-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Promote {selected.size} student{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              These students will move from <b>{srcLabel}</b> to <b>{dstLabel}</b>. Their class, section & session update everywhere.
              {crossSession && <> Each student's current outstanding due carries forward as a <b>"{oldDuesLabel.trim() || "Old Dues"}"</b> line in the new session.</>}
              {" "}Transport will be {applyTransport ? "kept" : "removed"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="promote-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doPromote(); }} disabled={promoting} data-testid="promote-confirm">
              {promoting ? <Loader2 size={16} className="animate-spin mr-1.5" /> : null}Confirm Promotion
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
